import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { categoryService, Category } from '@/services/categoryService'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { Badge } from '@/components/ui/badge'
import { Plus, Edit, Trash2, Eye, Image as ImageIcon } from 'lucide-react'
import { getImageUrl } from '@/config/imageConfig'

const CategoryList = () => {
  const navigate = useNavigate()
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)

  useEffect(() => {
    loadCategories()
  }, [search, statusFilter, page, pageSize])

  const loadCategories = async () => {
    try {
      setLoading(true)
      const filters: any = {}
      if (search) filters.search = search
      if (statusFilter !== 'all') filters.is_active = statusFilter === 'active'
      
      const response = await categoryService.getAll({
        ...filters,
        page,
        limit: pageSize
      })
      
      setCategories(response.data)
      setTotalItems(response.total || response.data.length)
    } catch (error) {
      console.error('Error loading categories:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      try {
        await categoryService.delete(id)
        loadCategories()
      } catch (error) {
        console.error('Error deleting category:', error)
        alert('Échec de la suppression de la catégorie')
      }
    }
  }

  const handleView = (category: Category) => {
    navigate(`/categories/${category.category_id}`)
  }

  const handleEdit = (category: Category) => {
    navigate(`/categories/${category.category_id}/edit`)
  }

  const columns = [
    {
      key: 'logo_url',
      label: 'Logo',
      sortable: false,
      render: (value: string | null) => {
        const logoUrl = value ? getImageUrl(`/uploads/categories/${value}`) : null
        return logoUrl ? (
          <img
            src={logoUrl}
            alt="Logo de la catégorie"
            className="h-10 w-10 object-cover rounded-lg border border-gray-200"
          />
        ) : (
          <div className="h-10 w-10 flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
            <ImageIcon className="h-5 w-5 text-gray-400" />
          </div>
        )
      }
    },
    {
      key: 'name',
      label: 'Nom',
      sortable: true,
      render: (value: string) => (
        <span className="font-medium">{value}</span>
      )
    },
    {
      key: 'description',
      label: 'Description',
      sortable: false,
      render: (value: string) => (
        <span className="text-gray-600 truncate max-w-xs">{value || '-'}</span>
      )
    },
    {
      key: 'parent_category_id',
      label: 'Catégorie parente',
      sortable: false,
      render: (value: string) => (
        <span>{value ? `ID parent : ${value}` : 'Racine'}</span>
      )
    },
    {
      key: 'is_active',
      label: 'Statut',
      sortable: true,
      render: (value: boolean) => (
        <Badge variant={value ? 'default' : 'secondary'}>
          {value ? 'Actif' : 'Inactif'}
        </Badge>
      )
    },
    {
      key: 'created_at',
      label: 'Créé le',
      sortable: true,
      render: (value: string) => (
        <span>{new Date(value).toLocaleDateString()}</span>
      )
    }
  ]

  const actions = [
    {
      label: 'Voir',
      icon: <Eye className="h-4 w-4" />,
      onClick: handleView,
      variant: 'outline' as const
    },
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: handleEdit,
      variant: 'outline' as const
    },
    {
      label: 'Supprimer',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (row: Category) => handleDelete(row.category_id),
      variant: 'destructive' as const
    }
  ]

  const filters = [
    {
      key: 'search',
      label: 'Recherche',
      type: 'text' as const,
      placeholder: 'Rechercher des catégories...'
    },
    {
      key: 'status',
      label: 'Statut',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous' },
        { value: 'active', label: 'Actif' },
        { value: 'inactive', label: 'Inactif' }
      ]
    }
  ]

  const handleFilterChange = (filters: any[]) => {
    filters.forEach(filter => {
      if (filter.key === 'search') {
        setSearch(filter.value)
        setPage(1)
      } else if (filter.key === 'status') {
        setStatusFilter(filter.value)
        setPage(1)
      }
    })
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    setPage(1)
  }

  const breadcrumbs = [
    { label: 'Tableau de bord', href: '/' },
    { label: 'Catégories' }
  ]

  const headerActions = [
    {
      label: 'Ajouter une catégorie',
      onClick: () => navigate('/categories/new'),
      variant: 'default' as const,
      icon: <Plus className="h-4 w-4" />
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Catégories"
        subtitle="Gérez les catégories et sous-catégories de produits"
        breadcrumbs={breadcrumbs}
        actions={headerActions}
      />

      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onSearch={(value) => {
          setSearch(value)
          setPage(1)
        }}
        searchPlaceholder="Rechercher des catégories..."
      />

      <DataTable
        columns={columns}
        data={categories}
        actions={actions}
        loading={loading}
        pagination={{
          current: page,
          total: totalItems,
          pageSize: pageSize,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
        emptyMessage="Aucune catégorie trouvée"
      />
    </div>
  )
}

export default CategoryList
